// What an edition changed: the sentences it lost and gained against the one
// before it, and its instructions against theirs. Read when the dialog opens.

import type { FormDiffLine, FormTextDiff } from "@duralex/shared";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { useEditionChanges } from "../../hooks/useForms";
import { fmtDate } from "../../lib/dates";

function DiffList({
  glyph,
  title,
  lines,
}: {
  glyph: string;
  title: string;
  lines: FormDiffLine[];
}) {
  return (
    <>
      <h5 className="forms-diff-title">
        {title} ({lines.length})
      </h5>
      {lines.length === 0 ? (
        <p className="forms-muted">None.</p>
      ) : (
        <ul className="forms-diff-lines">
          {lines.map((line, i) => (
            <li key={i}>
              <span className="glyph" aria-hidden="true">
                {glyph}
              </span>
              <span className="page tnum">p. {line.page}</span>
              <span className="text">{line.text}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function DiffSection({ title, diff }: { title: string; diff: FormTextDiff }) {
  const empty = diff.removed.length === 0 && diff.added.length === 0;
  // The server cuts each list at its cap, so a cut answer's longer list is the cap.
  const cap = Math.max(diff.removed.length, diff.added.length);
  return (
    <section className="settings-section forms-diff">
      <h4 className="settings-label">{title}</h4>
      {diff.reason === "first" && (
        <p className="settings-hint">The first recorded edition: nothing to compare.</p>
      )}
      {diff.reason === "unread" && (
        <p className="settings-hint">
          One of the two texts has not been read, so there is nothing to compare.
        </p>
      )}
      {diff.reason === null && (
        <>
          <p className="forms-muted">
            {diff.changedAt ? (
              <>
                Compared with the instructions read before{" "}
                <span className="tnum">{fmtDate(diff.changedAt)}</span>.
              </>
            ) : (
              `Compared with ${diff.against}.`
            )}
          </p>
          {empty ? (
            <p className="settings-hint">No sentence differs.</p>
          ) : (
            <>
              {diff.truncated && (
                <p className="forms-muted">Only the first {cap} lines of a list are shown.</p>
              )}
              <DiffList glyph="-" title="Removed" lines={diff.removed} />
              <DiffList glyph="+" title="Added" lines={diff.added} />
            </>
          )}
        </>
      )}
    </section>
  );
}

export function EditionChanges({ formId, editionKey }: { formId: string; editionKey: string }) {
  const changesQ = useEditionChanges(formId, editionKey);
  if (changesQ.isPending) {
    return (
      <SkeletonGroup label="Reading what changed">
        <Skeleton width="70%" />
        <Skeleton width="55%" />
      </SkeletonGroup>
    );
  }
  if (changesQ.isError) {
    return (
      <p className="settings-error" role="alert">
        Could not read what changed.
      </p>
    );
  }
  return (
    <>
      <DiffSection title="Form" diff={changesQ.data.form} />
      {changesQ.data.instructions && (
        <DiffSection title="Instructions" diff={changesQ.data.instructions} />
      )}
    </>
  );
}
